import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, X } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useTeamMember, useUser } from '../../store/useAuthStore';

interface AppBanner { id: string; message: string }

/**
 * Admin-posted banners (Admin → Banners) across the top of every page. Each
 * one stays until the viewer closes it or an admin ends it. Cached across page
 * changes: every route mounts its own Layout.
 */
export function AppBanners() {
  const user = useUser();
  const teamMember = useTeamMember();
  const queryClient = useQueryClient();
  const queryKey = ['banners', 'mine', user?._id, teamMember?._id];
  const { data: banners = [] } = useQuery<AppBanner[]>({
    queryKey,
    enabled: !!user && user.role !== 'admin',
    staleTime: 5 * 60 * 1000,
    queryFn: async () => (await apiClient.get<{ success: boolean; data: AppBanner[] }>('/banners/mine')).data ?? []
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => apiClient.post(`/banners/${id}/dismiss`, {}),
    // Gone at once; a failed save only means it comes back on the next load.
    onMutate: (id: string) => queryClient.setQueryData<AppBanner[]>(queryKey, (current?: AppBanner[]) => (current ?? []).filter((b: AppBanner) => b.id !== id))
  });

  if (!banners.length) return null;
  return (
    <div className="space-y-px" role="region" aria-label="Announcements">
      {banners.map((banner: AppBanner) => (
        <div key={banner.id} className="flex items-start gap-3 px-4 py-2.5 text-sm sm:px-6"
          style={{ background: 'var(--info-light)', color: 'var(--info-dark)', borderBottom: '1px solid var(--neutral-200)' }}>
          <Megaphone aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="min-w-0 flex-1 whitespace-pre-line break-words">{banner.message}</p>
          <button type="button" onClick={() => dismiss.mutate(banner.id)} aria-label="Dismiss announcement"
            className="-m-1 shrink-0 rounded p-1 transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-current">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
